import { useState, useEffect, useRef } from "react";
import "./payrollSettings.css";
import "../../companyDetails.css";
import T from "utils/constants/T";
import { get } from "lodash";
import MainCard from "components/MainCard";
import {
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { useDispatch } from "react-redux";
import authApi from "interceptor/authInterceptor";
import { processMyobAccessToken, storeMyobCompanyDetails } from "store/reducers/myob";
import { fetchCompanyDetails } from "store/reducers/companyTabDetails";
import toast from "utils/ToastNotistack";

interface ViewPayrollSettingsMYOBBusinessProps {
  companyName: string;
  companyEmail: string;
  companyPhone: string;
  abn: string;
  systemName: string;
  selectedCompanyId: string;
  role: string;
  handleSyncMyob: () => void;
}

const ViewPayrollSettingsMYOBBusiness = ({
  companyName = "",
  companyEmail = "",
  companyPhone = "",
  abn = "",
  systemName = "",
  selectedCompanyId = "",
  role = "",
  handleSyncMyob,
}: ViewPayrollSettingsMYOBBusinessProps) => {
  const dispatch = useDispatch();
  const [isReauthorizing, setIsReauthorizing] = useState(false);

  const checkPopupClosedRef = useRef<any>(null);
  const handleMessageRef = useRef<any>(null);

  // Clean up interval and listeners on unmount
  useEffect(() => {
    return () => {
      if (checkPopupClosedRef.current) {
        clearInterval(checkPopupClosedRef.current);
      }
      if (handleMessageRef.current) {
        window.removeEventListener("message", handleMessageRef.current);
      }
    };
  }, []);

  const handleReauth = async () => {
    if (!selectedCompanyId) {
      toast("Please select a company first.", { variant: "error" });
      return;
    }

    setIsReauthorizing(true);
    try {
      const response = await authApi.get("/v2/api/myob/getOauthUrl?isReauth=true");
      const { oauthUrl } = response?.data?.data || {};

      if (oauthUrl) {
        const popup = window.open(
          oauthUrl,
          "myobAuth",
          "resizable,scrollbars=0,status,top=100,left=200,width=800,height=400"
        );
        if (!popup) {
          toast("Popup blocked. Please allow popups and try again.", {
            variant: "error",
          });
          setIsReauthorizing(false);
          return;
        }
        popup?.focus();
        toast("MYOB authorization opened successfully.", { variant: "success" });

        const handleMessage = async (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;

          const data = event.data;
          if (typeof data === "string" && data.includes("MYOBCODE")) {
            const code = data.split("MYOBCODE=")[1];
            window.removeEventListener("message", handleMessage);
            handleMessageRef.current = null;
            if (checkPopupClosedRef.current) {
              clearInterval(checkPopupClosedRef.current);
              checkPopupClosedRef.current = null;
            }

            try {
              const tokenResponse: any = await dispatch(
                (processMyobAccessToken as any)(code, true)
              );

              if (tokenResponse) {
                const files = get(tokenResponse, "companyFiles", []);
                const payload = {
                  companyId: selectedCompanyId,
                  isReauth: true,
                  accessToken: tokenResponse.accessToken,
                  refreshToken: tokenResponse.refreshToken,
                  expiresIn: tokenResponse.expiresIn,
                  companyFileId: tokenResponse.companyFileId || (files.length > 0 ? files[0].Id : ""),
                  companyName: companyName || tokenResponse.companyName || "",
                  companyAbn: abn || "",
                  companyEmail: companyEmail || "",
                  companyPhone: companyPhone || "",
                };

                await dispatch((storeMyobCompanyDetails as any)(payload));
                toast("MYOB Business authorization updated successfully.", { variant: "success" });
                // Refresh company details tab
                dispatch((fetchCompanyDetails as any)(selectedCompanyId));
              }
            } catch (err: any) {
              toast(err?.response?.data?.message || err?.message || "MYOB authentication failed.", {
                variant: "error",
              });
            } finally {
              setIsReauthorizing(false);
            }
          }
        };

        window.addEventListener("message", handleMessage);
        handleMessageRef.current = handleMessage;

        checkPopupClosedRef.current = setInterval(() => {
          if (!popup || popup.closed) {
            if (checkPopupClosedRef.current) {
              clearInterval(checkPopupClosedRef.current);
              checkPopupClosedRef.current = null;
            }
            setIsReauthorizing(false);
            window.removeEventListener("message", handleMessage);
            handleMessageRef.current = null;
          }
        }, 1000);
      } else {
        toast("Failed to generate MYOB auth URL.", { variant: "error" });
        setIsReauthorizing(false);
      }
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to connect to MYOB.";
      toast(message, { variant: "error" });
      setIsReauthorizing(false);
    }
  };

  const hasAccess = role === "superadmin" || role === "employer";

  return (
    <MainCard
      title={systemName}
      secondary={
        hasAccess && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              className="main-card-button"
              onClick={handleSyncMyob}
              disabled={isReauthorizing}
            >
              {T.SYNC_PAYROLL}
            </Button>
            <Button
              variant="contained"
              className="main-card-button"
              onClick={handleReauth}
              disabled={isReauthorizing}
            >
              {isReauthorizing ? <CircularProgress size={20} color="inherit" /> : T.reAuth}
            </Button>
          </Stack>
        )
      }
    />
  );
};

export default ViewPayrollSettingsMYOBBusiness;
